import React, { useState } from "react";
import {
  Box,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import DriveFileRenameOutlineRoundedIcon from "@mui/icons-material/DriveFileRenameOutlineRounded";
import ImageRoundedIcon from "@mui/icons-material/ImageRounded";
import SellRoundedIcon from "@mui/icons-material/SellRounded";
import BannerPicker from "../pieces/BannerPicker";
import TagChips from "../pieces/TagChips";

const CATEGORIES = [
  "Festa",
  "Show",
  "Pagode",
  "Eletrônica",
  "Sertanejo",
  "Esporte",
  "Teatro",
  "Outros",
];
const CLASSIFICATIONS = ["Livre", "10", "12", "14", "16", "18"];

const sectionTitleSx = {
  fontWeight: 900,
  fontSize: "1rem",
  color: "rgba(255,255,255,0.94)",
  mb: 0.45,
};

const sectionSubSx = {
  fontSize: "0.84rem",
  color: "rgba(255,255,255,0.5)",
  mb: 2,
  lineHeight: 1.6,
};

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "16px",
    backgroundColor: "rgba(255,255,255,0.03)",
    transition: "all 0.2s ease-in-out",
    "& fieldset": {
      borderColor: "rgba(255,255,255,0.12)",
    },
    "&:hover fieldset": {
      borderColor: "rgba(255,255,255,0.24)",
    },
    "&.Mui-focused": {
      backgroundColor: "rgba(255,255,255,0.05)",
      "& fieldset": {
        borderColor: "rgba(0,225,255,0.55)",
      },
    },
  },
  "& .MuiInputLabel-root": {
    color: "rgba(255,255,255,0.58)",
  },
  "& .MuiInputBase-input": {
    color: "#fff",
  },
};

const blockSx = {
  p: { xs: 1.4, md: 1.8 },
  borderRadius: 2,
  border: "1px solid rgba(255,255,255,0.08)",
  bgcolor: "rgba(255,255,255,0.025)",
};

function SectionIntro({ icon, title, subtitle }) {
  return (
    <Stack spacing={1.25} sx={{ mb: 2 }}>
      <Stack direction="row" spacing={1.1} alignItems="center">
        <Box
          sx={{
            width: 38,
            height: 38,
            borderRadius: 2,
            display: "grid",
            placeItems: "center",
            bgcolor: "rgba(0,225,255,0.12)",
            color: "#8ef4ff",
          }}
        >
          {icon}
        </Box>
        <Box>
          <Typography sx={sectionTitleSx}>{title}</Typography>
          <Typography sx={sectionSubSx}>{subtitle}</Typography>
        </Box>
      </Stack>
    </Stack>
  );
}

export default function IdentidadeStep({ state, actions, errors }) {
  const [tagInput, setTagInput] = useState("");

  return (
    <Stack spacing={2}>
      <Box sx={blockSx}>
        <SectionIntro
          icon={<DriveFileRenameOutlineRoundedIcon sx={{ fontSize: 20 }} />}
          title="Informações principais"
          subtitle="Os dados desta seção ajudam seu público a identificar o evento com rapidez."
        />

        <Grid container spacing={1.6}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Título do evento"
              placeholder="Ex: Sexta do Pagode no Quati Valley"
              value={state.identidade.titulo}
              onChange={(e) => actions.set("identidade.titulo", e.target.value)}
              error={!!errors["identidade.titulo"]}
              helperText={errors["identidade.titulo"] || "Use um nome forte, claro e fácil de lembrar."}
              sx={inputSx}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              select
              fullWidth
              label="Categoria"
              value={state.identidade.categoria}
              onChange={(e) => actions.set("identidade.categoria", e.target.value)}
              error={!!errors["identidade.categoria"]}
              helperText={errors["identidade.categoria"]}
              sx={inputSx}
              InputLabelProps={{ shrink: true }}
            >
              {CATEGORIES.map((c) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              select
              fullWidth
              label="Classificação"
              value={state.identidade.classificacao}
              onChange={(e) => actions.set("identidade.classificacao", e.target.value)}
              sx={inputSx}
              InputLabelProps={{ shrink: true }}
            >
              {CLASSIFICATIONS.map((c) => (
                <MenuItem key={c} value={c === "Livre" ? "Livre" : c}>
                  {c === "Livre" ? "Livre (Todas as idades)" : `${c} anos`}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </Box>

      <Box sx={blockSx}>
        <SectionIntro
          icon={<ImageRoundedIcon sx={{ fontSize: 20 }} />}
          title="Banner do evento"
          subtitle="A imagem vai aparecer na listagem e no topo da página pública do evento."
        />

        <BannerPicker
          state={state}
          actions={actions}
          error={errors["identidade.banner"]}
        />
      </Box>

      <Box sx={blockSx}>
        <SectionIntro
          icon={<SellRoundedIcon sx={{ fontSize: 20 }} />}
          title="Tags de busca"
          subtitle="Essas palavras ajudam na busca e reforçam o contexto do evento."
        />

        <TagChips
          tags={state.identidade.tags}
          value={tagInput}
          onChange={setTagInput}
          onAdd={(t) => actions.set("identidade.tags", [...state.identidade.tags, t])}
          onRemove={(idx) =>
            actions.set(
              "identidade.tags",
              state.identidade.tags.filter((_, i) => i !== idx),
            )
          }
        />
      </Box>
    </Stack>
  );
}
